import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import {
  DefinitionPane,
  PaneType,
  ProcessedClause,
  isDefinitionPane,
  isRulePane,
} from "@financial-advisory/data-access/api-types";
import {
  selectActiveClause,
  selectActivePaneId,
  setActiveClauseById,
  setActivePaneId,
  useGetHighlightsQuery,
  useGetProcessedClausesQuery,
  useUpdateProcessedClauseTreeMutation,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { PageSpinner } from "@financial-advisory/ui/components/spinners";
import { ReactFlowProvider } from "@xyflow/react";
import {
  ClausePlaceholder,
  HighlightColorKeys,
  PdfViewer,
} from "../components";
import { GraphHeader } from "../components/GraphHeader";
import { EditableTreeFlow, LayoutFlow } from "../components/ReactFlow";
import { DocumentViewerHeader } from "../headers/DocumentViewerHeader";
import { useRegulations } from "../providers/RegulationsProvider";

type TreeKind = "trigger" | "obligations" | "definition";

interface EditableTreeTarget {
  paneId: string;
  treeKind: TreeKind;
  rootNode: Record<string, unknown>;
}

export const RegulationsPage = () => {
  const dispatch = useDispatch();
  const { regulation } = useRegulations();
  const activePaneId = useSelector(selectActivePaneId);
  const [editingTarget, setEditingTarget] = useState<EditableTreeTarget | null>(
    null
  );
  const activeClause: ProcessedClause | undefined =
    useSelector(selectActiveClause);
  const [updateTree] = useUpdateProcessedClauseTreeMutation();
  const {
    isLoading: isLoadingProcessedClauses,
    refetch: refetchProcessedClauses,
  } = useGetProcessedClausesQuery(
    {
      regulationId: regulation?.pdfFileName ?? "",
    },
    { skip: !regulation }
  );
  const { data: highlights, isLoading: isLoadingHighlights } =
    useGetHighlightsQuery(
      { regulationId: regulation?.pdfFileName ?? "" },
      {
        skip: !regulation,
      }
    );

  useEffect(() => {
    // We only allow querying on a definition, if there is exactly one, set that to the active pane id
    const definitionPanes = activeClause?.panes.filter(
      (pane) => pane.paneType === PaneType.DEFINITION
    );
    if (definitionPanes?.length === 1) {
      dispatch(setActivePaneId(definitionPanes[0].id));
    } else {
      dispatch(setActivePaneId(null));
    }
  }, [dispatch, activeClause]);

  // Reset the highlight id when the regulation changes
  useEffect(() => {
    dispatch(setActiveClauseById(null));
  }, [dispatch, regulation?.pdfFileName]);

  // Reset editing target when the user switches to a different clause so we don't send the old
  // clause's tree to the new clause's API (data corruption).
  useEffect(() => {
    setEditingTarget(null);
  }, [activeClause?.recordId]);

  const regulationId = regulation?.pdfFileName ?? "";
  const processedClauseId = activeClause?.recordId ?? "";

  const editableTargets = useMemo((): EditableTreeTarget[] => {
    const panes = activeClause?.panes ?? [];
    const targets: EditableTreeTarget[] = [];
    for (const pane of panes) {
      if (isDefinitionPane(pane)) {
        const def = (pane as DefinitionPane).definition as unknown as Record<
          string,
          unknown
        >;
        const rootNode = (def.rootNode ?? def.root_node) as Record<
          string,
          unknown
        >;
        if (rootNode) {
          targets.push({
            paneId: pane.id,
            treeKind: "definition",
            rootNode,
          });
        }
      } else if (isRulePane(pane)) {
        const rule = pane.rule as unknown as Record<string, unknown>;
        const obligations = rule?.obligations as
          | Record<string, unknown>
          | undefined;
        const obligationsTree = obligations?.tree as
          | Record<string, unknown>
          | undefined;
        const obligationsRoot = (obligationsTree?.rootNode ??
          obligationsTree?.root_node) as Record<string, unknown> | undefined;
        if (obligationsRoot) {
          targets.push({
            paneId: pane.id,
            treeKind: "obligations",
            rootNode: obligationsRoot,
          });
        }
        const evaluatedTrigger = rule?.evaluatedTrigger as
          | Record<string, unknown>
          | undefined;
        const triggerRoot = (evaluatedTrigger?.rootNode ??
          evaluatedTrigger?.root_node) as Record<string, unknown> | undefined;
        if (triggerRoot) {
          targets.push({
            paneId: pane.id,
            treeKind: "trigger",
            rootNode: triggerRoot,
          });
        }
      }
    }
    return targets;
  }, [activeClause?.panes]);

  const targetToEdit = useMemo((): EditableTreeTarget | null => {
    if (editableTargets.length === 0) return null;
    if (activePaneId) {
      const forPane = editableTargets.filter((t) => t.paneId === activePaneId);
      if (forPane.length > 0) {
        return forPane[0];
      }
    }
    // Default: prefer the rule pane's obligations tree (the main REQUIREMENT tree the user sees)
    // so we don't open the definition pane's smaller tree when the visible content is the rule.
    const obligationsFirst = editableTargets.find(
      (t) => t.treeKind === "obligations"
    );
    if (obligationsFirst) return obligationsFirst;
    return editableTargets[0];
  }, [editableTargets, activePaneId]);

  const saveTree = useCallback(
    async (params: {
      regulationId: string;
      processedClauseId: string;
      paneId: string;
      treeKind: "trigger" | "obligations" | "definition";
      rootNode: Parameters<typeof updateTree>[0]["rootNode"];
    }) => {
      await updateTree(params).unwrap();
      await refetchProcessedClauses();
    },
    [updateTree, refetchProcessedClauses]
  );

  if (
    isLoadingProcessedClauses ||
    isLoadingHighlights ||
    !regulation?.pdfFileName
  ) {
    return <PageSpinner />;
  }

  return (
    <>
      <DocumentViewerHeader />
      <div className="flex flex-row h-full">
        <div className="relative flex flex-col flex-1 w-[45%] h-full bg-gray-200 border-r border-gray-200">
          <HighlightColorKeys />
          <div className="relative flex-1">
            <ErrorBoundary fallback={<div>Error loading PDF</div>}>
              <PdfViewer
                documentName={regulation.pdfFileName}
                highlights={highlights?.data || []}
              />
            </ErrorBoundary>
          </div>
        </div>
        <div className="flex flex-col relative w-[55%] h-full">
          <GraphHeader
            onEditTree={
              targetToEdit && regulationId && processedClauseId
                ? () => setEditingTarget(targetToEdit)
                : undefined
            }
          />
          {activeClause ? (
            editingTarget && regulationId && processedClauseId ? (
              <ReactFlowProvider key="editable-tree">
                <EditableTreeFlow
                  initialRootNode={editingTarget.rootNode}
                  paneId={editingTarget.paneId}
                  treeKind={editingTarget.treeKind}
                  productTreeId={editingTarget.paneId}
                  regulationId={regulationId}
                  processedClauseId={processedClauseId}
                  onSave={() => setEditingTarget(null)}
                  onCancel={() => setEditingTarget(null)}
                  saveTree={saveTree}
                  className="h-auto flex-1"
                />
              </ReactFlowProvider>
            ) : (
              <ReactFlowProvider key={activeClause?.id ?? "no-clause"}>
                <LayoutFlow
                  panes={activeClause?.panes ?? []}
                  value={[]}
                  className="h-auto flex-1"
                />
              </ReactFlowProvider>
            )
          ) : (
            <ClausePlaceholder />
          )}
        </div>
      </div>
    </>
  );
};
